import { FC, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import { Button } from "antd";
import { TimeStep } from "constants/charts.enums";
import { selectAllCharts, setDataSourceSetting, updateChartByPath } from "store/features";
import { RootState } from "store/rootReducer";
import styled from "styled-components";

import useFeatureFlags from "hooks/useFeatureFlags";

import { TimeScaleSelect, TimeStepIcon } from "components/shared";

const TimeStepSelect: FC = () => {
  const dispatch = useDispatch();
  const { hasFeature } = useFeatureFlags();

  // state from store
  const dataSourceSetting = useSelector(
    (state: RootState) => state.app.pdenDataSourceSetting
  );
  const allCharts = useSelector(selectAllCharts);

  function handleTimeStepChange(value: TimeStep): void {
    const nextSourceSetting = {
      ...dataSourceSetting,
      timeStep: value
    };

    dispatch(setDataSourceSetting(nextSourceSetting));

    // updating all new charts
    Object.keys(allCharts).forEach((chartId) => {
      dispatch(
        updateChartByPath({
          id: chartId,
          path: "features.timeStep.properties.value",
          value: value
        })
      );
    });
  }

  const isDailyDataToggleEnabled = hasFeature("Daily Data Toggles");

  // EVA-10: a global timestep setting persisted as Day or Hybrid would
  // otherwise keep requesting daily-resolution data for every chart even
  // after "Daily Data Toggles" is disabled and this selector (the user's
  // only way to change it) is hidden below. Hybrid includes daily-resolution
  // data (see TimeScaleSelect), so it must be reset alongside Day. Force it
  // back to the monthly default.
  //
  // Review fix: call handleTimeStepChange's dispatches directly (rather than
  // depend on the function reference, which is recreated every render) so
  // the deps array captures everything the effect body actually reads —
  // matches the pattern in TimeStepToggle.tsx's equivalent effect.
  //
  // Review fix: only dispatch to charts whose own persisted timestep
  // (features.timeStep.properties.value — same path TimeStepToggle.tsx reads
  // via useChartEntities) is Day or Hybrid. Previously every chart in
  // allCharts was dispatched to on mount, including charts already at Month,
  // producing an unnecessary dispatch burst. TimeStepToggle's own per-chart
  // clamp is left in place — it covers contexts where this global selector
  // isn't mounted.
  useEffect(() => {
    if (
      !isDailyDataToggleEnabled &&
      (dataSourceSetting.timeStep === TimeStep.Day ||
        dataSourceSetting.timeStep === TimeStep.Hybrid)
    ) {
      const nextSourceSetting = {
        ...dataSourceSetting,
        timeStep: TimeStep.Month
      };

      dispatch(setDataSourceSetting(nextSourceSetting));

      Object.entries(allCharts).forEach(([chartId, chart]) => {
        const chartTimeStep = chart?.features?.timeStep?.properties?.value;
        if (chartTimeStep === TimeStep.Day || chartTimeStep === TimeStep.Hybrid) {
          dispatch(
            updateChartByPath({
              id: chartId,
              path: "features.timeStep.properties.value",
              value: TimeStep.Month
            })
          );
        }
      });
    }
  }, [isDailyDataToggleEnabled, dataSourceSetting, allCharts, dispatch]);

  if (!isDailyDataToggleEnabled) return null;

  return (
    <TimeScaleSelect
      trigger={["click"]}
      value={dataSourceSetting.timeStep}
      onChange={handleTimeStepChange}
      isDataSourcePublic={dataSourceSetting.source === "Public"}
      isDataSourcePrivate={dataSourceSetting.source === "Private"}>
      <StyledButton icon={TimeStepIcon[dataSourceSetting.timeStep]} />
    </TimeScaleSelect>
  );
};

const StyledButton = styled(Button)`
  background: "transparent";
  color: #a2aaad;
  &:hover {
    color: "var(--color-text)";
  }
`;

export default TimeStepSelect;
